import { IProduct, IQuestionnaire, prepareQuestionnaire } from './../Model/Questionnaire'
import { observable, computed, action, toJS, reaction } from 'mobx'
import {
    Questionnaire,
    QuestionnaireStatus,
    Passport,
    IQuestionnaireComment,
    Phone,
    Client,
    Address,
    PhoneType
} from '../Model/Questionnaire'
import DateTimeHelper from '../Helpers/DateTimeHelper'
import * as Moment from 'moment'
import LocalizationStore from './LocalizationStore'
// import { PaymentScheduleItemModel } from '../Model/PaymentScheduleItemModel'
import * as _ from 'lodash'
import { QuestionnairePrefillData } from '../Model/Questionnaire'
import { createStub } from '../Model/QuestionnaireTableModel'
import ActionsLocalService from '../Services/ActionsLocalService'
import { ExportType } from './ExportStore'
import { CreditCondition, ICreditCondition } from '../Model/CreditCondition'
import { IAgentInfo } from '../Model/AgentInfo'
import { CourtDecision } from '../Model/CourtDecision'

export interface ItemByTypeAndPageCount {
    items: Array<IQuestionnaire>,
    pageCount: number
}

export interface ItemsByTypeAndPageCount {
    All: ItemByTypeAndPageCount,
    Draft: ItemByTypeAndPageCount,
    InProgressCreditConsultant: ItemByTypeAndPageCount,
    Scored: ItemByTypeAndPageCount,
    InProgressCreditExpert: ItemByTypeAndPageCount,
    Find: ItemByTypeAndPageCount,
}

export default class QuestionnairesStore {

    @observable listByTypeAndPageCount: ItemsByTypeAndPageCount = {
        All: {
            items: [],
            pageCount: 0
        },
        Draft: {
            items: [],
            pageCount: 0
        },
        InProgressCreditConsultant: {
            items: [],
            pageCount: 0
        },
        Scored: {
            items: [],
            pageCount: 0
        },
        InProgressCreditExpert: {
            items: [],
            pageCount: 0
        },
        Find: {
            items: [],
            pageCount: 0
        }
    }

    @observable
    private item: Questionnaire

    @observable
    private prefillData: QuestionnairePrefillData

    @observable
    private currentNotPaidPenalty: number

    @observable
    private creditLimit: number

    private static _instance: QuestionnairesStore

    private constructor() {
        reaction(
            () => this.listByTypeAndPageCount.All,
            (x) => {
                ActionsLocalService.getInstance()
                    .setExportData(ExportType.QAll, createStub(this.listByTypeAndPageCount.All.items))
            }
        )

        reaction(
            () => this.listByTypeAndPageCount.Draft,
            (x) => {
                ActionsLocalService.getInstance()
                    .setExportData(ExportType.QDraft, createStub(this.listByTypeAndPageCount.Draft.items))
            }
        )

        reaction(
            () => this.listByTypeAndPageCount.InProgressCreditConsultant,
            (x) => {
                ActionsLocalService.getInstance()
                    .setExportData(
                        ExportType.QInProgressCreditConsultant,
                        createStub(this.listByTypeAndPageCount.InProgressCreditConsultant.items)
                    )
            }
        )

        reaction(
            () => this.listByTypeAndPageCount.InProgressCreditExpert,
            (x) => {
                ActionsLocalService.getInstance()
                    .setExportData(
                        ExportType.QInProgressCreditExpert,
                        createStub(this.listByTypeAndPageCount.InProgressCreditExpert.items)
                    )
            }
        )

        reaction(
            () => this.listByTypeAndPageCount.Scored,
            (x) => {
                ActionsLocalService.getInstance()
                    .setExportData(ExportType.QScored, createStub(this.listByTypeAndPageCount.Scored.items))
            }
        )

        reaction(
            () => this.listByTypeAndPageCount.Find,
            (x) => {
                ActionsLocalService.getInstance()
                    .setExportData(ExportType.QFind, createStub(this.listByTypeAndPageCount.Find.items))
            }
        )
    }

    public static getInstance(): QuestionnairesStore {
        return this._instance || (this._instance = new this())
    }

    changeStatus(s: QuestionnaireStatus): void {
        return
    }

    @action.bound
    setPrefillData(prefillData: QuestionnairePrefillData): void {
        let q = new Questionnaire()

        q.client.passport = new Passport()
        q.client.passport.pin = prefillData.pin
        q.client.phones = new Array<Phone>()
        q.client.phones.push(
            {
                id: '',
                number: prefillData.phoneNumber,
                phoneType: 0,
                isDefaultPhone: false
            })
        q.credit.creditCondition.termCountMonths = prefillData.termCountMonths

        this.prefillData = prefillData

        this.setItem(q)
    }

    @action.bound
    setListByTypeAndPageCount(items: ItemsByTypeAndPageCount) {
        this.listByTypeAndPageCount = items
    }

    @computed get getListByTypeAndPageCount() {
        return this.listByTypeAndPageCount
    }

    @action.bound
    setCreditLimit(creditLimitByEmail: number) {
        this.creditLimit = creditLimitByEmail
    }

    @computed get getCreditLimit() {
        return toJS(this.creditLimit)
    }

    @computed
    get getPrefillData(): QuestionnairePrefillData {
        return this.prefillData
    }

    setPrefillDataFromExternalSource(data: Object | null, prefillData: QuestionnairePrefillData): void {
        let q = Questionnaire.create(prefillData)
        let regSeries = /^[a-zA-Z]{2,}/g
        let regNumber = /[\d]/g

        if (data) {
            let seriesMatches = data['passportSeria'].match(regSeries)
            let numberMatches = data['passportSeria'].match(regNumber)
            q.client = new Client()
            q.client.passport.pin = data['pin']
            q.client.name = data['firstName']
            q.client.middleName = data['middleName']
            q.client.surname = data['lastName']
            q.client.birthday = DateTimeHelper.formatDateForList(data['dateOfBirth'])
            q.client.passport.series = !!seriesMatches ? seriesMatches.join('') : ''
            q.client.passport.number = !!numberMatches ? numberMatches.join('') : ''
            q.client.addresses = []
            q.client.phones.push(
                new Phone(prefillData.phoneNumber ? prefillData.phoneNumber + '' : '', PhoneType.MobilePhone))

            try {
                let address = new Address()
                let matches = data['address'].toString().split(',')
                address.cityName = matches[0]
                address.streetName = matches[1]
                q.client.addresses.push(address)

                // if (prefillData! && prefillData!.phoneNumber) {
                //     q.client.phones.push(new Phone(prefillData!.phoneNumber + '', PhoneType.MobilePhone))
                // }
            } catch (error) {
                console.info(LocalizationStore.getInstance().t('Error setting prefill data'))
            }
        }

        this.setItem(q)
    }

    cloneData(formData: any): void {
        let q = new Questionnaire()

        q.client.passport = new Passport()

        this.setItem(q)
    }

    @action.bound
    addCommentToItem(comment: IQuestionnaireComment) {
        this.item.comments.push(comment)
    }

    @action.bound
    setItem(q?: Questionnaire | null) {
        q = prepareQuestionnaire(q)
        this.item = q!
    }

    @action.bound
    setClonedItem(q: Questionnaire) {
        let cloned = toJS(q)

        delete cloned.id
        delete cloned.client.id
        delete cloned.client.passport.id
        delete cloned.credit.id
        delete cloned.credit.creditNumber
        delete cloned.credit.creditCondition.paymentsScheduleJson
        delete cloned.credit.creditCondition.id
        delete cloned.credit.creditDetail.id
        delete cloned.status
        delete cloned.credit.penalties
        delete cloned.credit.payments

        cloned.client.addresses = cloned.client.addresses.map(
            x => {
                delete x.id
                return x
            })

        cloned.client.phones = cloned.client.phones.map(x => {
            delete x.id
            return x
        })

        cloned.credit.products = cloned.credit.products.map(x => {
            delete x.id
            return x
        })

        try {
            cloned.client.birthday = Moment(q.client.birthday, 'DD.MM.YYYY').format()
            cloned.createDate = Moment(q.createDate, 'DD.MM.YYYY').format()
            cloned.credit.createDate = Moment(q.credit.createDate, 'DD.MM.YYYY').format()
        } catch (error) {
            console.info(error, 'cloning error')
        }

        cloned.orderLink = ''
        cloned.orderSource = ''
        cloned.dpdScore = ''
        cloned.questionnaireUserHistoryItems = []
        cloned.contentLinks = []
        cloned.comments = []
        cloned.dpdScore = ''

        this.setItem(cloned)
    }

    @action.bound
    addItemContentLink(qId: string, links: any) {
        this.item.contentLinks = this.item.contentLinks.concat(toJS(links.contentLinks))
        this.item = toJS(this.item)
    }

    @action.bound
    deleteItemContentLink(clId: string) {
        this.item.contentLinks = toJS(this.item.contentLinks.filter((cl) => { return cl.id !== clId }))
    }

    @action.bound
    updateItemContentLink(q: Questionnaire) {
        this.item = q
    }

    @computed
    get getItem(): Questionnaire {
        return toJS(this.item)
    }

    setCreditCondition(q: Questionnaire): Questionnaire {
        if (!!q.credit.creditCondition) {
            q.credit.creditCondition = new CreditCondition()
        }
        return q
    }

    @action.bound
    updateProductList(product: IProduct) {
        let q = _.cloneDeep(toJS(this.item))
        if (!q.credit.products) {
            q.credit.products = []
        }

        let i = _.findIndex(q.credit.products, (o) => { return o.externalKey === product.externalKey })
        if (i === -1 && product.count > 0) {
            q.credit.products.push(product)
        } else if (!q.credit.products[i]) {
            return
        } else if (q.credit.products[i].count + product.count < 1) {
            q.credit.products = q.credit.products.filter((p) => {
                return p.externalKey !== product.externalKey
            })
        } else {
            q.credit.products[i].count = q.credit.products[i].count + product.count
        }

        this.item = q
    }

    @action.bound
    setCurrentNotPaidPenalty(penalty: number) {
        this.currentNotPaidPenalty = penalty
    }

    @computed
    get getCurrentNotPaidPenalty() {
        return this.currentNotPaidPenalty
    }

    @action.bound
    updateCreditCondition(condition: ICreditCondition) {
        let q = _.cloneDeep(toJS(this.item))
        if (!q) {
            return
        }
        q.credit.creditCondition = condition
        this.item = q
    }

    @action.bound
    updateAgentInfo(agentInfo: IAgentInfo) {
        let q = _.cloneDeep(toJS(this.item))
        if (!q || (q.agentCode === agentInfo.agentCode && q.agentAction === agentInfo.agentAction)) {
            return
        }
        q.agentCode = agentInfo.agentCode
        q.agentAction = agentInfo.agentAction
        this.item = q
    }

    @action.bound
    updateCourtDecision(courtDecision: CourtDecision) {
        let q = _.cloneDeep(toJS(this.item))
        if (!q) {
            return
        }
        q.credit.courtDecision = courtDecision
        this.item = q
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Stores/QuestionnairesStore.ts